import { TransportError, type FetcherKind } from './errors';
import type { Fetcher, FetchInit, FetchResult } from './fetcher';
import { backoffDelay, DEFAULT_RETRY, Semaphore, sleep, type RetryPolicy } from './retry';
import { ReachabilityStore, type EndpointClass } from './reachability';
import { utf8Decode } from '../bytes/encoding';

export interface RetryInfo {
  logId: string;
  url: string;
  attempt: number;
  delayMs: number;
  status: number;
}

export interface LogTransportOptions {
  /** Fetchers in preference order (evaluated per request so settings changes apply immediately). */
  fetchers: () => Fetcher[];
  reachability?: ReachabilityStore;
  retry?: Partial<RetryPolicy>;
  onRetry?: (info: RetryInfo) => void;
  /** Max concurrent requests per log. */
  concurrency?: number;
  /** How long a recorded CORS block keeps us from retrying the direct fetcher first. */
  blockMemoryMs?: number;
}

const RETRYABLE = new Set([429, 503]);
/** A CORS block seen this recently is not re-probed when the direct fetcher is the only option. */
const FAIL_FAST_MS = 60 * 60 * 1000;

/**
 * Performs log requests with: fetcher fallback (direct → proxy) on CORS failures, Retry-After
 * aware backoff on 429/503, per-log concurrency limits, and reachability bookkeeping.
 */
export class LogTransport {
  readonly reachability: ReachabilityStore;
  private readonly retry: RetryPolicy;
  private readonly semaphores = new Map<string, Semaphore>();

  constructor(private readonly opts: LogTransportOptions) {
    this.reachability = opts.reachability ?? new ReachabilityStore();
    this.retry = { ...DEFAULT_RETRY, ...opts.retry };
  }

  private sem(logId: string): Semaphore {
    let s = this.semaphores.get(logId);
    if (!s) {
      s = new Semaphore(this.opts.concurrency ?? 6);
      this.semaphores.set(logId, s);
    }
    return s;
  }

  private order(logId: string, cls: EndpointClass): Fetcher[] {
    const fetchers = this.opts.fetchers();
    const rec = this.reachability.get(logId, cls);
    const memory = this.opts.blockMemoryMs ?? 24 * 60 * 60 * 1000;
    const preferFallback =
      rec !== undefined &&
      Date.now() - rec.at < memory &&
      (rec.status === 'cors-blocked' || (rec.status === 'ok' && rec.via !== undefined && rec.via !== 'direct'));
    if (preferFallback && fetchers.some((f) => f.kind !== 'direct')) {
      // Known blocked (or last success came through a fallback): try the alternatives first, direct last.
      return [...fetchers.filter((f) => f.kind !== 'direct'), ...fetchers.filter((f) => f.kind === 'direct')];
    }
    return fetchers;
  }

  async request(log: { id: string }, cls: EndpointClass, url: string, init?: FetchInit): Promise<FetchResult> {
    const release = await this.sem(log.id).acquire();
    try {
      return await this.requestUnthrottled(log.id, cls, url, init);
    } finally {
      release();
    }
  }

  private async requestUnthrottled(logId: string, cls: EndpointClass, url: string, init?: FetchInit): Promise<FetchResult> {
    const fetchers = this.order(logId, cls);
    if (fetchers.length === 0) throw new TransportError('network', 'no fetchers configured', { url });
    // Known CORS block and nothing but the browser's own fetch available: fail immediately with the
    // explanation instead of waiting for the server's full response just to be blocked again.
    const rec = this.reachability.get(logId, cls);
    if (rec?.status === 'cors-blocked' && Date.now() - rec.at < FAIL_FAST_MS && fetchers.every((f) => f.kind === 'direct')) {
      throw new TransportError('cors-blocked', rec.error ?? `requests to ${url} are blocked by the browser (no CORS headers)`, { url, via: 'direct' });
    }
    let lastError: TransportError | undefined;
    for (const f of fetchers) {
      try {
        const res = await this.withRetries(logId, f, url, init);
        if (res.status >= 200 && res.status < 300) {
          this.reachability.set(logId, cls, { status: 'ok', via: f.kind, at: Date.now() });
          return res;
        }
        // A definite HTTP answer (404, 400, …) is an answer — do not fall through to other fetchers.
        const detail = errorDetail(res);
        const err = new TransportError(RETRYABLE.has(res.status) ? 'rate-limited' : 'http', `HTTP ${res.status} from ${url}${detail ? `: ${detail}` : ''}`, {
          url,
          status: res.status,
          via: f.kind,
          ...(res.retryAfterMs !== undefined ? { retryAfterMs: res.retryAfterMs } : {}),
          ...(detail ? { detail } : {}),
        });
        if (err.kind === 'http') this.reachability.set(logId, cls, { status: 'ok', via: f.kind, at: Date.now() });
        throw err;
      } catch (e) {
        const err = e instanceof TransportError ? e : new TransportError('network', String(e), { url, via: f.kind, cause: e });
        if (err.kind === 'aborted') throw err;
        if (err.kind === 'cors-blocked') {
          this.reachability.set(logId, cls, { status: 'cors-blocked', via: f.kind, at: Date.now(), error: err.message });
          lastError = err;
          continue; // next fetcher
        }
        if (err.kind === 'network' || err.kind === 'dns') {
          this.reachability.set(logId, cls, { status: 'error', via: f.kind, at: Date.now(), error: err.message });
          lastError = err;
          // A dead host is dead through a proxy too, so do not waste a round trip on it.
          if (err.kind === 'dns') break;
          continue;
        }
        throw err;
      }
    }
    throw lastError ?? new TransportError('network', 'request failed', { url });
  }

  private async withRetries(logId: string, f: Fetcher, url: string, init?: FetchInit): Promise<FetchResult> {
    for (let attempt = 1; ; attempt++) {
      const res = await f.fetch(url, init);
      if (!RETRYABLE.has(res.status) || attempt >= this.retry.maxAttempts) return res;
      const delayMs = backoffDelay(attempt, res.retryAfterMs, this.retry);
      this.opts.onRetry?.({ logId, url, attempt, delayMs, status: res.status });
      await sleep(delayMs, init?.signal);
    }
  }

  async json<T = unknown>(log: { id: string }, cls: EndpointClass, url: string, init?: FetchInit): Promise<{ value: T; res: FetchResult; text: string }> {
    const res = await this.request(log, cls, url, init);
    const text = utf8Decode(res.body);
    try {
      return { value: JSON.parse(text) as T, res, text };
    } catch (e) {
      throw new TransportError('parse', `invalid JSON from ${url}`, { url, via: res.via, cause: e });
    }
  }

  async text(log: { id: string }, cls: EndpointClass, url: string, init?: FetchInit): Promise<{ text: string; res: FetchResult }> {
    const res = await this.request(log, cls, url, init);
    return { text: utf8Decode(res.body), res };
  }
}

/** A short, single-line excerpt of a textual error body (JSON/text/HTML stripped of tags). */
function errorDetail(res: FetchResult): string | undefined {
  const ct = res.headers.get('content-type') ?? '';
  if (res.body.length === 0 || !/text|json|xml|html/i.test(ct)) return undefined;
  const text = utf8Decode(res.body.subarray(0, 600)).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, 200) : undefined;
}

export type { FetcherKind };
